import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "激情电影", to: "/category/movie" },
  { label: "激情短视频", to: "/category/short" },
  { label: "热门主题", to: "/category/theme" },
  { label: "关于我们", to: "/about" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  const solid = scrolled || !isHome || menuOpen;

  const submitSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const value = keyword.trim();
    setSearchOpen(false);
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search");
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          solid ? "bg-background-100/95 backdrop-blur-md border-b border-background-200/70" : "bg-transparent"
        }`}
      >
        <div className="flex items-center justify-between h-16 md:h-[72px] px-4 md:px-8 w-full">
          <Link to="/" className="flex items-center gap-2.5 shrink-0 group">
            <span className="w-9 h-9 rounded-md bg-primary-500 flex items-center justify-center text-background-50">
              <i className="ri-play-fill text-lg" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-xl md:text-2xl font-bold tracking-wide text-foreground-50">
                激情视频
              </span>
              <span className="hidden sm:block text-[10px] tracking-[0.22em] text-foreground-500 font-label uppercase mt-0.5">
                Passion Video
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => {
              const active =
                item.to === "/" ? location.pathname === "/" : location.pathname.startsWith(item.to.split("?")[0]);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`px-4 py-2 rounded-md text-sm transition-colors whitespace-nowrap ${
                    active
                      ? "text-accent-400 bg-background-200/60"
                      : "text-foreground-300 hover:text-foreground-50 hover:bg-background-200/40"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSearchOpen((prev) => !prev)}
              aria-label="搜索"
              className="w-9 h-9 rounded-md flex items-center justify-center text-foreground-200 hover:text-foreground-50 hover:bg-background-200/60 transition-colors cursor-pointer"
            >
              <i className={searchOpen ? "ri-close-line text-lg" : "ri-search-line text-lg"} />
            </button>
            <Link
              to="/category/theme"
              className="hidden md:flex items-center gap-1.5 h-9 px-4 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-fire-line" />
              热门片单
            </Link>
            <button
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              aria-label="菜单"
              className="lg:hidden w-9 h-9 rounded-md flex items-center justify-center text-foreground-100 hover:bg-background-200/60 transition-colors cursor-pointer"
            >
              <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className="border-t border-background-200/60 bg-background-100/98 px-4 md:px-8 py-3 animate-fade-in">
            <form onSubmit={submitSearch} className="flex items-center gap-2 max-w-3xl">
              <div className="flex-1 flex items-center gap-2 h-10 px-3 rounded-md bg-background-50 border border-background-300/70 focus-within:border-primary-400 transition-colors">
                <i className="ri-search-line text-foreground-500" />
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索片名、导演、标签，例如「午夜」"
                  className="flex-1 bg-transparent outline-none text-sm text-foreground-100 placeholder:text-foreground-600"
                />
              </div>
              <button
                type="submit"
                className="h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                搜索
              </button>
            </form>
          </div>
        )}

        {menuOpen && (
          <div className="lg:hidden border-t border-background-200/60 bg-background-100/98 px-4 py-3 animate-fade-in">
            <nav className="flex flex-col">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="flex items-center justify-between px-2 py-3 text-sm text-foreground-200 border-b border-background-200/50 last:border-0"
                >
                  {item.label}
                  <i className="ri-arrow-right-s-line text-foreground-500" />
                </Link>
              ))}
              <Link
                to="/search"
                className="mt-2 flex items-center justify-center gap-2 h-10 rounded-md bg-primary-500 text-background-50 text-sm font-medium"
              >
                <i className="ri-search-line" />
                全站搜索
              </Link>
            </nav>
          </div>
        )}
      </header>
      <div className="h-16 md:h-[72px]" />
    </>
  );
}